import type { LinkedIdentity } from '@orgus/protocol';
import { Tooltip } from '@orgus/ui';
import { ACCOUNT_TYPE_LABEL, providerLabel } from './labels.ts';
import { ProviderMark } from './ProviderMark.tsx';

/** Words for one linked account, for tooltips and screen readers. */
export function identityDescription(i: LinkedIdentity): string {
  return [
    `${providerLabel(i.provider)}: ${i.email ?? 'no email'}`,
    i.accountType ? ACCOUNT_TYPE_LABEL[i.accountType] : null,
    i.emailVerified ? null : 'Unverified email',
    i.anchor ? 'The account that admitted this member' : null,
    i.addedVia === 'invite' ? `Added by ${i.addedBy} through an invite` : null,
    i.awaitingConfirm ? (i.suspended ? `Stopped working: ${i.addedBy} can no longer manage this member, who never confirmed it` : 'Not confirmed by the member yet') : null,
  ]
    .filter(Boolean)
    .join('. ');
}

/**
 * The Google and Microsoft accounts a member signs in with, as one quiet group of provider marks
 * in the row's meta line (row budget: no chips). Each mark names its account on hover and focus.
 */
export function IdentityMarks({ identities }: { identities: LinkedIdentity[] }) {
  if (!identities.length) return null;
  return (
    <span className="inline-flex shrink-0 items-center gap-1" aria-label={`${identities.length} linked ${identities.length === 1 ? 'sign-in' : 'sign-ins'}`}>
      {identities.map((i) => (
        <Tooltip key={i.id} content={identityDescription(i)}>
          <span tabIndex={0} aria-label={identityDescription(i)} className="inline-flex rounded-control text-fg-subtle hover:text-fg-muted">
            <ProviderMark id={i.provider} className="size-3 shrink-0" />
          </span>
        </Tooltip>
      ))}
    </span>
  );
}
